import { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import type { AuditEntry, PaginatedResponse } from '../types'
import { getAdminToken, clearAdminToken } from './authToken'

const ACTION_LABELS: Record<string, { label: string; bg: string; text: string }> = {
  login:          { label: 'LOGIN',          bg: 'bg-blue-100',   text: 'text-blue-800' },
  view_list:      { label: 'VIEW LIST',      bg: 'bg-gray-100',   text: 'text-gray-800' },
  view_detail:    { label: 'VIEW DETAIL',    bg: 'bg-gray-100',   text: 'text-gray-700' },
  delete:         { label: 'DELETE',          bg: 'bg-red-100',    text: 'text-red-800' },
  export_csv:     { label: 'EXPORT CSV',     bg: 'bg-green-100',  text: 'text-green-800' },
  export_json:    { label: 'EXPORT JSON',    bg: 'bg-green-100',  text: 'text-green-800' },
  video_download: { label: 'VIDEO DL',       bg: 'bg-purple-100', text: 'text-purple-800' },
  cleanup:        { label: 'CLEANUP',        bg: 'bg-orange-100', text: 'text-orange-800' },
}

function formatDateTime(dateString: string) {
  const date = new Date(dateString + 'Z') // D1 stores UTC without timezone suffix
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

function getLoggedInName(): string | null {
  try {
    const token = getAdminToken()
    if (!token) return null
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.name || payload.username || null
  } catch {
    return null
  }
}

export default function AuditLog() {
  const [rows, setRows] = useState<AuditEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const loggedInName = getLoggedInName()

  const page = parseInt(searchParams.get('page') || '1')
  const limit = parseInt(searchParams.get('limit') || '50')
  const usernameFilter = searchParams.get('username') || ''
  const actionFilter = searchParams.get('action') || ''

  const updateParams = useCallback((updates: Record<string, string>) => {
    const next = new URLSearchParams(searchParams)
    for (const [k, v] of Object.entries(updates)) {
      if (v) next.set(k, v)
      else next.delete(k)
    }
    if (!('page' in updates)) next.set('page', '1')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const handleLogout = () => {
    clearAdminToken()
    navigate('/admin/login')
  }

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const token = getAdminToken()
      const params = new URLSearchParams()
      params.set('page', String(page))
      params.set('limit', String(limit))
      if (usernameFilter) params.set('username', usernameFilter)
      if (actionFilter) params.set('action', actionFilter)

      const r = await fetch(`/api/admin/audit?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!r.ok) throw new Error(`Failed to fetch: ${r.status}`)
      const j: PaginatedResponse<AuditEntry> = await r.json()
      setRows(j.items || [])
      setTotal(j.total || 0)
      setTotalPages(j.totalPages || 0)
    } catch (e) {
      Sentry.captureException(e, { tags: { action: 'load_audit_log' } })
      setError(e instanceof Error ? e.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [page, limit, usernameFilter, actionFilter])

  useEffect(() => { fetchData() }, [fetchData])

  return (
    <div className='min-h-screen p-6 bg-gradient-to-br from-slate-50 to-slate-100'>
      <div className='max-w-6xl mx-auto space-y-4'>
        {/* Header */}
        <div className='bg-white rounded-xl border-b border-gray-200 px-5 py-3'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-4'>
              <img src='/images/logo.png' alt='JointAi' className='h-7' />
              <nav className='flex items-center gap-3 text-sm'>
                <Link to='/admin' className='text-slate-400 hover:text-slate-600 transition-colors'>
                  Submissions
                </Link>
                <span className='text-slate-300'>/</span>
                <span className='text-slate-700 font-medium'>Audit Log</span>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              {loggedInName && (
                <span className="text-sm text-slate-500">Hi {loggedInName}</span>
              )}
              <button
                onClick={handleLogout}
                className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {error ? (
          <div className='bg-white rounded-xl shadow p-6'>
            <h2 className='text-xl font-bold text-red-600'>Error Loading Audit Log</h2>
            <p className='text-slate-600 mt-2'>{error}</p>
            <button onClick={() => { setError(null); fetchData() }} className='mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'>
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Filters */}
            <div className='bg-white rounded-xl shadow p-4'>
              <div className='flex flex-wrap items-center gap-3'>
                <select
                  value={usernameFilter}
                  onChange={(e) => updateParams({ username: e.target.value })}
                  className='px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500'
                >
                  <option value=''>All Users</option>
                  {/* Unique usernames from current data — sufficient for a small team */}
                  {[...new Set(rows.map(r => r.username))].sort().map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>

                <select
                  value={actionFilter}
                  onChange={(e) => updateParams({ action: e.target.value })}
                  className='px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500'
                >
                  <option value=''>All Actions</option>
                  <option value='login'>Login</option>
                  <option value='view_list'>View List</option>
                  <option value='delete'>Delete</option>
                  <option value='export_csv'>Export CSV</option>
                  <option value='export_json'>Export JSON</option>
                  <option value='video_download'>Video Download</option>
                  <option value='cleanup'>Cleanup</option>
                </select>

                <button onClick={fetchData} className='px-3 py-2 text-gray-500 hover:text-gray-700 text-sm'>
                  Refresh
                </button>
              </div>

              {(usernameFilter || actionFilter) && (
                <div className='mt-3 flex items-center gap-2 text-sm text-gray-600'>
                  <span>Showing {total} result{total !== 1 ? 's' : ''}</span>
                  {usernameFilter && (
                    <span className='bg-blue-50 text-blue-700 px-2 py-0.5 rounded'>
                      user: {usernameFilter}
                      <button onClick={() => updateParams({ username: '' })} className='ml-1 text-blue-400 hover:text-blue-600'>&times;</button>
                    </span>
                  )}
                  {actionFilter && (
                    <span className='bg-blue-50 text-blue-700 px-2 py-0.5 rounded'>
                      action: {actionFilter}
                      <button onClick={() => updateParams({ action: '' })} className='ml-1 text-blue-400 hover:text-blue-600'>&times;</button>
                    </span>
                  )}
                  <button
                    onClick={() => updateParams({ username: '', action: '' })}
                    className='text-blue-600 hover:underline'
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Table */}
            <div className='bg-white rounded-xl shadow'>
              {loading ? (
                <div className='text-center py-12'>
                  <p className='text-gray-500'>Loading audit log...</p>
                </div>
              ) : rows.length === 0 ? (
                <div className='text-center py-12'>
                  <p className='text-gray-400'>{usernameFilter || actionFilter ? 'No matching entries' : 'No audit entries yet'}</p>
                </div>
              ) : (
                <>
                  <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                      <thead className='text-gray-600 bg-gray-50 border-b'>
                        <tr>
                          <th className='text-left p-3 min-w-[150px]'>Date/Time</th>
                          <th className='text-left p-3'>User</th>
                          <th className='text-left p-3'>Action</th>
                          <th className='text-left p-3'>Target</th>
                          <th className='text-left p-3'>Detail</th>
                          <th className='text-left p-3'>IP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(r => {
                          const cfg = ACTION_LABELS[r.action] || { label: r.action.toUpperCase(), bg: 'bg-gray-100', text: 'text-gray-700' }
                          return (
                            <tr key={r.id} className='border-t border-gray-100 hover:bg-gray-50'>
                              <td className='p-3 text-xs text-gray-700 whitespace-nowrap'>
                                {formatDateTime(r.created_at)}
                              </td>
                              <td className='p-3 font-medium text-gray-900'>{r.username}</td>
                              <td className='p-3'>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
                                  {cfg.label}
                                </span>
                              </td>
                              <td className='p-3 text-gray-600 font-mono text-xs'>
                                {r.target_id ? (
                                  r.action === 'delete' ? (
                                    <span className='text-red-600'>{r.target_id}</span>
                                  ) : (
                                    <Link to={`/admin/${r.target_id}`} className='text-blue-600 hover:underline'>
                                      {r.target_id.length > 12 ? r.target_id.slice(0, 12) + '...' : r.target_id}
                                    </Link>
                                  )
                                ) : (
                                  <span className='text-gray-300'>-</span>
                                )}
                              </td>
                              <td className='p-3 text-xs text-gray-600 max-w-[200px] truncate' title={r.detail || ''}>
                                {r.detail || <span className='text-gray-300'>-</span>}
                              </td>
                              <td className='p-3 text-xs text-gray-400 font-mono'>{r.ip}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className='flex items-center justify-between px-4 py-3 border-t border-gray-100'>
                    <div className='text-sm text-gray-600'>
                      Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} of {total}
                    </div>
                    <div className='flex items-center gap-2'>
                      <select
                        value={limit}
                        onChange={(e) => updateParams({ limit: e.target.value, page: '1' })}
                        className='px-2 py-1 border border-gray-300 rounded text-sm bg-white'
                      >
                        <option value='25'>25</option>
                        <option value='50'>50</option>
                        <option value='100'>100</option>
                      </select>
                      <span className='text-sm text-gray-500'>per page</span>

                      <div className='flex items-center gap-1 ml-4'>
                        <button
                          onClick={() => updateParams({ page: '1' })}
                          disabled={page <= 1}
                          className='px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-30 hover:bg-gray-50'
                        >
                          First
                        </button>
                        <button
                          onClick={() => updateParams({ page: String(page - 1) })}
                          disabled={page <= 1}
                          className='px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-30 hover:bg-gray-50'
                        >
                          Prev
                        </button>
                        <span className='px-3 py-1 text-sm text-gray-600'>
                          Page {page} of {totalPages}
                        </span>
                        <button
                          onClick={() => updateParams({ page: String(page + 1) })}
                          disabled={page >= totalPages}
                          className='px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-30 hover:bg-gray-50'
                        >
                          Next
                        </button>
                        <button
                          onClick={() => updateParams({ page: String(totalPages) })}
                          disabled={page >= totalPages}
                          className='px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-30 hover:bg-gray-50'
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
